import React, { useEffect, useState, useCallback } from 'react';
import { Home, Download, FileText, Image, Music, Video, HardDrive, Star, PinOff } from 'lucide-react';
import { useFileManagerStore } from '../../store/useFileManagerStore';
import { TreeNode } from '../UI/FolderTree';
import { FileStat } from '../../api/IFileSystem';
import { WebRTCSystem } from '../../api/WebRTCSystem';
import { t } from '../../i18n';

const Sidebar: React.FC = () => {
  const { currentPath, setCurrentPath, quickAccessItems, refreshQuickAccess, unpinFolder, fs } = useFileManagerStore();
  const [drives, setDrives] = useState<FileStat[]>([]);

  const loadData = useCallback(async () => {
    // Only load if connected (for WebRTCSystem)
    if (fs instanceof WebRTCSystem && !fs.isConnected) {
      return;
    }
    try {
      await refreshQuickAccess();
      const driveList = await fs.getDrives();
      if (driveList.length > 0) {
        setDrives(driveList);
      }
    } catch (e) {
      console.warn('[Sidebar] Failed to load data:', e);
    }
  }, [fs, refreshQuickAccess]);

  useEffect(() => {
    loadData();

    // Retry loading periodically if drives are empty (connection might not be ready)
    const retryInterval = setInterval(() => {
      if (drives.length === 0) {
        loadData();
      }
    }, 2000);

    return () => clearInterval(retryInterval);
  }, [loadData, drives.length]);

  const getIcon = (name: string) => {
      const n = name.toLowerCase();
      if (n.includes('desktop')) return Home;
      if (n.includes('download')) return Download;
      if (n.includes('document')) return FileText;
      if (n.includes('picture')) return Image;
      if (n.includes('music')) return Music;
      if (n.includes('video')) return Video;
      return Star; 
  };

  const isProtected = (name: string) => ['desktop', 'downloads'].includes(name.toLowerCase());

  return (
    <div className="w-64 flex flex-col glass h-full select-none overflow-y-auto custom-scrollbar pb-4">
      {/* Quick Access Section */}
      <div className="pt-2">
        <div className="text-xs font-semibold text-slate-400 px-4 py-2 uppercase tracking-wider">{t('quickAccess')}</div>
        
        {/* 此电脑 (Root) */}
        <div
          onClick={() => setCurrentPath('root')}
          className={`flex items-center px-4 py-1.5 cursor-pointer transition-colors group relative mx-2 rounded-md ${
              currentPath === 'root' ? 'bg-white/10 text-win-accent' : 'hover:bg-white/5'
          }`}
        >
          <HardDrive size={16} className="mr-3 flex-shrink-0" />
          <span className="text-sm truncate flex-1">{t('rootLabel')}</span>
        </div>

        {quickAccessItems.map(item => {
            const Icon = getIcon(item.name);
            const protectedItem = isProtected(item.name);
            return (
            <div
            key={item.path}
            onClick={() => setCurrentPath(item.path)}
            title={item.path}
            className={`flex items-center px-4 py-1.5 cursor-pointer transition-colors group relative mx-2 rounded-md ${
                currentPath === item.path ? 'bg-white/10 text-win-accent' : 'hover:bg-white/5'
            }`}
            >
            <Icon size={16} className="mr-3 flex-shrink-0" />
            <span className="text-sm truncate flex-1">{item.name}</span>
            
            {!protectedItem && (
                <button 
                    onClick={(e) => { e.stopPropagation(); unpinFolder(item.path); }}
                    className="opacity-0 group-hover:opacity-100 p-1 hover:bg-white/10 rounded text-slate-400 hover:text-white transition-all absolute right-2"
                    title={t('unpinQuickAccess')}
                >
                    <PinOff size={14} />
                </button>
            )}
            </div>
        )})}
      </div>

      {/* This PC Tree Section */}
      <div className="mt-2">
        <div 
          onClick={() => setCurrentPath('root')}
          className="text-xs font-semibold text-slate-400 px-4 py-2 uppercase tracking-wider cursor-pointer hover:text-white transition-colors"
        >
          {t('rootLabel')}
        </div>
        <div className="px-2">
            {drives.length === 0 ? (
                <div className="flex items-center px-2 py-1.5 text-slate-500">
                    <div className="w-3 h-3 border border-slate-500 border-t-transparent rounded-full animate-spin mr-2" />
                    <span className="text-sm">{t('loading')}</span>
                </div>
            ) : (
                drives.map(drive => (
                    <TreeNode key={drive.path} file={drive} />
                ))
            )}
        </div>
      </div>
    </div>
  );
};

export default Sidebar;
